// workflowStepNodeUtils.js

import {
  getMissionTaskPersonKey,
  normalizeMissionPeople,
} from 'missions/taskmodal/missionTaskModalUtils';
import { getNodeType } from 'services/nodetypes/NodeTypesCacheService';
import { resolveSchemaFieldValue } from 'views/utils/fieldValueResolver';

const HOST_PEOPLE_FIELD_NAMES = [
  'assignees',
  'assignee',
  'people',
  'owners',
  'owner',
  'members',
];

/**
 * resolveTaskPeopleFieldName
 *
 * Prefers `assignees`, then `assignee`, else the first people field on the TASK type.
 *
 * @param {object|null} typeDef
 * @returns {string}
 */
export function resolveTaskPeopleFieldName(typeDef = null) {
  const fields = Array.isArray(typeDef?.schema?.fields) ? typeDef.schema.fields : [];
  const peopleFields = fields.filter((field) => String(field?.type || '').trim() === 'people');

  const assigneesField = peopleFields.find((field) => (
    String(field?.name || '').trim().toLowerCase() === 'assignees'
  ));
  if (assigneesField) return 'assignees';

  const assigneeField = peopleFields.find((field) => (
    String(field?.name || '').trim().toLowerCase() === 'assignee'
  ));
  if (assigneeField) return 'assignee';

  return String(peopleFields[0]?.name || '').trim() || 'assignees';
}

/**
 * readHostNodePeople
 *
 * Collects people summaries from the host record that owns a workflow run
 * (schema people fields, then common data_* names, then derived peopleKeys).
 * Not PROJECT-specific — any template host type can supply owners.
 *
 * @param {object|null} node - Host record node.
 * @returns {object[]} Normalized mission people summaries.
 */
export function readHostNodePeople(node = null) {
  if (!node || typeof node !== 'object') return [];

  const seen = new Set();
  const people = [];

  const pushPeople = (raw) => {
    const list = Array.isArray(raw) ? raw : raw ? [raw] : [];
    const normalized = normalizeMissionPeople(
      list.map((entry) => {
        if (typeof entry === 'string' || typeof entry === 'number') {
          const key = String(entry || '').trim();
          return key ? { key, userKey: key } : null;
        }
        return entry;
      }).filter(Boolean),
    );

    normalized.forEach((person) => {
      const key = getMissionTaskPersonKey(person);
      if (!key || seen.has(key)) return;
      seen.add(key);
      people.push(person);
    });
  };

  const typeKey = String(node?.type || '').trim();
  if (typeKey) {
    const typeDef = getNodeType(typeKey) || null;
    const fields = Array.isArray(typeDef?.schema?.fields) ? typeDef.schema.fields : [];
    fields
      .filter((field) => String(field?.type || '').trim().toLowerCase() === 'people')
      .forEach((fieldDef) => {
        pushPeople(resolveSchemaFieldValue({
          dataSection: typeDef?.dataSection,
          fieldDef,
          item: node,
          typeKey,
        }));
      });
  }

  if (!people.length) {
    Object.keys(node).forEach((sectionKey) => {
      if (!sectionKey.startsWith('data_')) return;
      const section = node[sectionKey];
      if (!section || typeof section !== 'object' || Array.isArray(section)) return;

      HOST_PEOPLE_FIELD_NAMES.forEach((fieldName) => {
        if (Object.prototype.hasOwnProperty.call(section, fieldName)) {
          pushPeople(section[fieldName]);
        }
      });
    });
  }

  if (!people.length && Array.isArray(node.peopleKeys)) {
    pushPeople(node.peopleKeys);
  }

  return people;
}

/**
 * buildHostLaunchAssigneesByTaskKey
 *
 * Maps every people-handled template task to host-record people, with optional
 * per-task launch overrides.
 *
 * @param {object|null} template - Workflow template.
 * @param {object|null} node - Host record node.
 * @param {object|null} overridesByTaskKey - Optional launch-time overrides.
 * @returns {Record<string, object[]>}
 */
export function buildHostLaunchAssigneesByTaskKey(
  template = null,
  node = null,
  overridesByTaskKey = null,
) {
  const hostPeople = readHostNodePeople(node);
  const overrides = (
    overridesByTaskKey
    && typeof overridesByTaskKey === 'object'
  ) ? overridesByTaskKey : null;
  const next = {};

  (Array.isArray(template?.tasks) ? template.tasks : []).forEach((task) => {
    const taskKey = String(task?.key || '').trim();
    if (!taskKey) return;

    const handledBy = String(task?.handledBy || '').trim().toLowerCase();
    if (handledBy === 'bot') return;

    if (overrides && Object.prototype.hasOwnProperty.call(overrides, taskKey)) {
      next[taskKey] = normalizeMissionPeople(overrides[taskKey]);
      return;
    }

    next[taskKey] = hostPeople;
  });

  return next;
}

/**
 * readWorkflowStepNodeAssignees
 *
 * @param {object|null} stepNode
 * @returns {object[]}
 */
export function readWorkflowStepNodeAssignees(stepNode = null) {
  if (!stepNode || typeof stepNode !== 'object') return [];

  const dataBag = stepNode.data_task && typeof stepNode.data_task === 'object'
    ? stepNode.data_task
    : null;
  if (!dataBag) return [];

  return normalizeMissionPeople(
    dataBag.assignees ?? dataBag.assignee ?? dataBag.people ?? null
  );
}

/**
 * applyWorkflowStepNodeToRunTask
 *
 * Overlays live TASK substance onto a run step for display / assignee checks.
 *
 * @param {object|null} runTask
 * @param {object|null} stepNode
 * @returns {object|null}
 */
export function applyWorkflowStepNodeToRunTask(runTask = null, stepNode = null) {
  if (!runTask) return null;
  if (!stepNode) return runTask;

  const title = String(stepNode?.title || '').trim();
  const subtitle = String(stepNode?.subtitle || '').trim();
  const assignees = readWorkflowStepNodeAssignees(stepNode);

  return {
    ...runTask,
    title: title || runTask.title,
    description: subtitle || runTask.description,
    assignees: assignees.length > 0 ? assignees : runTask.assignees,
    nodeKey: String(stepNode?.key || runTask?.nodeKey || '').trim() || runTask.nodeKey,
  };
}

/**
 * collectWorkflowStepNodeKeys
 *
 * @param {object|null} missionRun
 * @returns {string[]}
 */
export function collectWorkflowStepNodeKeys(missionRun = null) {
  const tasks = Array.isArray(missionRun?.tasks) ? missionRun.tasks : [];
  const keys = [];
  const seen = new Set();

  tasks.forEach((task) => {
    const nodeKey = String(task?.nodeKey || '').trim();
    if (!nodeKey || seen.has(nodeKey)) return;
    seen.add(nodeKey);
    keys.push(nodeKey);
  });

  return keys;
}
